import * as ngCommonHttp from '@angular/common/http';

import { catchError, map } from 'rxjs/operators';

import { Injectable } from '@angular/core';
import { CatalogSearchApi } from '@constants/api';
import { Composite } from '@interfaces/composite/1.0';
import { ModifierGroup } from '@interfaces/modifier-group/1.0';
import { Modifier } from '@interfaces/modifier/1.0';
import { ProductGroup } from '@interfaces/product-group/1.0';
import { Product } from '@interfaces/product/1.0';
import { BehaviorSubject, of, throwError } from 'rxjs';
import { Observable } from 'rxjs/internal/Observable';
import { AbstractCatalogSearchService } from '@abstract-services/catalog-search/1.0/catalog-search.abstract-service';
import {
  CatalogSearchItem,
  CatalogSearchRequestBody,
  CatalogSearchResponse,
} from '@interfaces/catalog-search/1.0';
import { CatalogEntryParentsResponse } from '@generated/tcea/models/catalog-entry-parents-response';

@Injectable({
  providedIn: 'root',
})
export class CatalogSearchService implements AbstractCatalogSearchService {
  cachedParams: CatalogSearchRequestBody;
  cachedBrandId: string;
  serviceURL: any;
  masterCache: CatalogItemsMasterCache[];
  skuListCache = new BehaviorSubject<any>(undefined);

  constructor(private http: ngCommonHttp.HttpClient) {
    this.serviceURL = CatalogSearchApi.POST;

    if (!this.masterCache) {
      this.masterCache = [];
    }
  }

  getParents(catalogEntryId: string): Observable<CatalogEntryParentsResponse> {
    const url = CatalogSearchApi.GET_PARENTS.replace('{catalogEntryId}', catalogEntryId);

    return this.http.get(url).pipe(
      map((response: any) => {
        if (response && response._embedded) {
          return response;
        }

        return { _embedded: { catalogEntries: [] } };
      }),
      catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
    );
  }

  createCacheId(body: CatalogSearchRequestBody): string {
    const formattedValues = Object.keys(body).reduce((arr, key) => {
      if (body[key] && body[key]?.value !== 'all') {
        arr.push(body[key]);
      }

      return arr;
    }, []);
    const computedString = formattedValues.join('');

    return computedString.replace(/ /g, '-');
  }

  search(brandId: string, body: CatalogSearchRequestBody): Observable<CatalogSearchItem[]> {
    const URL = this.serviceURL;

    if (this.cachedBrandId !== brandId) {
      this.resetMasterCache();
      this.resetParams();
      this.cachedBrandId = brandId;
    }

    const cacheId = this.createCacheId(body);
    const cachedResults = this.getCache(cacheId);

    if (cachedResults) {
      return of(cachedResults);
    }

    this.cachedParams = body;

    return this.http.post(URL, body).pipe(
      map((response: CatalogSearchResponse) => {
        if (response && response._embedded) {
          this.updateItemCache(cacheId, response._embedded.catalog);
          return response._embedded.catalog;
        }

        return [];
      }),
      catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
    );
  }

  /**
   * Get the cached results for the cacheId
   * @param cacheId - id for the previously cached results
   */
  getCache(cacheId: string): CatalogSearchItem[] | undefined {
    return this.masterCache[cacheId];
  }

  /**
   * Updates the value in the cache for the given cachedId.
   * @param cachedId - id to use for storing results
   * @param results - results to store
   */
  updateItemCache(cachedId: string, results: CatalogSearchItem[]): void {
    this.masterCache[cachedId] = results;
  }

  resetMasterCache(): void {
    this.masterCache = [];
  }

  resetParams(): void {
    this.cachedParams = undefined;
  }
}

export interface CatalogItemsMasterCache {
  product?: Product[];
  productGroup?: ProductGroup[];
  modifier?: Modifier[];
  modifierGroup?: ModifierGroup[];
  composite?: Composite[];
}
